import { useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'
import { Card, EmptyState, ErrorBanner, Spinner, formatDateTime } from './common'
import { TaskTable } from './patching/Jobs'
import { MachinePatches } from './patching/Machines'
import { StoriaPatchMacchina } from './patching/Lifecycle'
import { NewJobWizard } from './patching/NewJobWizard'
import type { WizardPrefill } from './patching/NewJobWizard'
import { TaskDetail } from './patching/TaskDetail'
import { ReadinessChips, platformLabel, reasonHint, reasonLabel } from './patching/labels'

/**
 * The «Patching» section of a machine's page (ADR-0024): can it be patched
 * now (agent, environment tag), what it misses, install from here through the
 * same guided job as the Patching page, and the history of its installations.
 */
export function SezionePatchingCI({ ciId, scrive }: {
  ciId: string
  platform?: string
  scrive: boolean
}) {
  const readiness = useAsync(() => api.patchReadiness(ciId), [ciId])
  const tasks = useAsync(() => api.patchTasks({ ciId }), [ciId])
  const [wizard, setWizard] = useState<WizardPrefill | null>(null)
  const [detail, setDetail] = useState<string | null>(null)
  const [missingKey, setMissingKey] = useState(0)
  const r = readiness.data
  const running = (tasks.data ?? []).find((x) => x.status === 'running')

  return (
    <Card title={t('Patching')}
      actions={scrive ? (
        <>
          <button className="secondary" onClick={() => setWizard({ kind: 'reboot', ciIds: [ciId] })}>{t('Riavvia')}</button>
          <button onClick={() => setWizard({ ciIds: [ciId] })}>{t('Nuovo intervento')}</button>
        </>
      ) : undefined}>
      {/* A load error (licence, permission) must show: without it the section
          would only look empty and the buttons would seem to do nothing. */}
      <ErrorBanner error={readiness.error ?? tasks.error} />
      {r && (
        <div className="patch-ci-head">
          <span>{platformLabel(r.platform)}{r.environment ? ` · ${r.environment}` : ''}</span>
          <ReadinessChips reasons={r.reasons} missingTags={r.missing_tags} />
          {r.agent_last_seen && (
            <span className="text-muted">{t('agente sentito {d}', { d: formatDateTime(r.agent_last_seen) })}</span>
          )}
          {r.platform === 'windows' && r.software_managers != null && (
            <span className="text-muted" title={t('Con cosa l’agente aggiorna i programmi di terze parti su questa macchina.')}>
              {r.software_managers.length
                ? t('programmi con {m}', { m: r.software_managers.map((m) => (m === 'choco' ? 'Chocolatey' : m)).join(' + ') })
                : t('programmi: né winget né Chocolatey (solo Chrome ed Edge)')}
            </span>
          )}
          {running && (
            <button className="link" onClick={() => setDetail(running.id)}>{t('installazione in corso: dettagli')}</button>
          )}
        </div>
      )}
      {r && r.reasons.length > 0 && (
        <ul className="patch-reasons">
          {r.reasons.map((x) => <li key={x}><strong>{reasonLabel(x, r.missing_tags)}</strong> — {reasonHint(x)}</li>)}
        </ul>
      )}
      {!scrive && (
        <p className="field__hint">{t('Sola lettura: per installare serve il permesso di scrittura sul modulo «Patching».')}</p>
      )}
      <MachinePatches key={missingKey} ciId={ciId} canWrite={scrive} onNewJob={(p) => setWizard(p ?? { ciIds: [ciId] })} />

      <h4 className="patch-machine__title">{t('Installazioni')}</h4>
      {tasks.loading && !tasks.data ? <Spinner /> : (tasks.data ?? []).length === 0 ? (
        <EmptyState title={t('Nessuna installazione su questa macchina.')} />
      ) : (
        <TaskTable tasks={tasks.data ?? []} onOpen={setDetail} />
      )}

      <h4 className="patch-machine__title">{t('Storia delle patch')}</h4>
      <p className="field__hint" style={{ marginTop: 0 }}>{t('Ogni patch che è mancata su questa macchina: da quando, quando è stata corretta, se è tornata.')}</p>
      <StoriaPatchMacchina ciId={ciId} />
      <p className="field__hint"><Link to="/patching">{t('Tutte le macchine e gli interventi in Patching')}</Link></p>

      {wizard && (
        <NewJobWizard prefill={wizard} fleet={null} catalog={null} onClose={() => setWizard(null)}
          onCreated={() => {
            setWizard(null)
            void tasks.reload()
            void readiness.reload()
          }} />
      )}
      {detail && (
        <TaskDetail taskId={detail} onClose={() => setDetail(null)}
          onChanged={() => { void tasks.reload(); setMissingKey((k) => k + 1) }} />
      )}
    </Card>
  )
}
